import { Type, type Static } from '@sinclair/typebox';
import {
  defineSchema,
  SystemFields,
  StringField,
  SlugField,
  IntegerField,
  BooleanField,
  RelationOne,
  RelationField,
} from '@ayocore/platform/common/helpers/schema-types';
import accountingDocumentTypeSchemaDefinition from './document-type.schema';

import { name as moduleName } from '../module.json';
/**
 * Статусы документов — этапы жизненного цикла для конкретного типа документа.
 *
 * Каждый тип документа (INVOICE_DOC, ORDER_DOC, и т.д.) имеет свой набор статусов.
 * Статусы регистрируются через defineDocumentStatus() в setup модуля.
 */
export const AccountingDocumentStatusSchema = Type.Object({
  ...SystemFields,

  slug: SlugField({
    title: 'Slug',
    description: 'Уникальный код статуса в рамках типа документа (например, DRAFT, SENT, PAID).',
  }),

  name: StringField({
    title: 'Название',
    minLength: 1,
    maxLength: 100,
    description: 'Отображаемое название статуса.',
  }),

  color: StringField({
    title: 'Цвет',
    nullable: true,
    maxLength: 50,
    description: 'Цвет для отображения в интерфейсе (Mantine color name или hex).',
  }),

  sort_order: IntegerField({
    title: 'Порядок сортировки',
    nullable: true,
    description: 'Порядок отображения статуса в воронке.',
  }),

  is_final: BooleanField({
    title: 'Финальный',
    default: false,
    description:
      'Финальный статус (успех или отмена). Документ в финальном статусе считается завершённым.',
  }),

  is_negative: BooleanField({
    title: 'Негативный',
    default: false,
    description: 'Негативный финальный статус (отмена, отклонение).',
  }),

  is_active: BooleanField({
    title: 'Активный',
    default: true,
    description: 'Неактивные статусы скрыты из выбора, но исторические данные сохраняются.',
  }),

  description: StringField({
    title: 'Описание',
    nullable: true,
    maxLength: 500,
    description: 'Опциональное описание статуса.',
  }),

  document_type: RelationOne(accountingDocumentTypeSchemaDefinition),
  document_type_id: RelationField(accountingDocumentTypeSchemaDefinition, 'ID типа документа', {
    description: 'Тип документа, к которому относится статус.',
    relationName: 'document_type',
  }),
});

export type AccountingDocumentStatus = Static<typeof AccountingDocumentStatusSchema>;

export default defineSchema({
  moduleName,
  tableName: 'document_statuses',
  schema: AccountingDocumentStatusSchema,
  additionalIndexes: [
    'sort_order',
    'is_active',
    { columns: ['document_type_id', 'slug'], unique: true },
  ],
  dependencies: [accountingDocumentTypeSchemaDefinition.tableName],
});
